import { AnnotationTool } from '@cornerstonejs/tools';
import type { Types as CoreTypes } from '@cornerstonejs/core';
import type { UltrasoundRegion } from '@/models/dicom';
import type {
  SpectralCalibration,
  SpectralDopplerValues,
  SpectralDopplerToolVariant,
} from '@/models/spectralDoppler';
import { pointNearCanvas } from '../../utils/annotationAtPoint';

// Cornerstone's AnnotationTool is exported with strict typings that don't
// align with how the subclasses declare callbacks. Each subclass previously
// did `const BaseAnnotationTool = AnnotationTool as any` to opt out; the
// abstract base centralizes that single escape so subclasses can extend a
// shared identifier. The `any` instance type is intentional — tightening
// will follow once the shared lifecycle methods land on the base.
type AnnotationToolCtor = new (...args: any[]) => any;
const AnnotationToolUntyped = AnnotationTool as unknown as AnnotationToolCtor;

/** Shared base for the spectral Doppler tools (VelocityProbe, VelocitySegment, VTITrace). */
export abstract class SpectralDopplerBaseTool extends AnnotationToolUntyped {
  // A point under the label wins the press over the label.
  getHandleNearImagePoint(
    element: HTMLDivElement,
    annotation: Parameters<typeof pointNearCanvas>[1],
    canvasCoords: CoreTypes.Point2,
    proximity: number
  ) {
    return (
      pointNearCanvas(element, annotation, canvasCoords, proximity) ??
      super.getHandleNearImagePoint(
        element,
        annotation,
        canvasCoords,
        proximity
      )
    );
  }
}

/** Convert a Cornerstone world point → image (column, row) pixel coords. */
export function worldToImagePixel(
  worldPoint: CoreTypes.Point3,
  imageData: { worldToIndex: (p: CoreTypes.Point3) => CoreTypes.Point3 }
): [number, number] {
  const idx = imageData.worldToIndex(worldPoint);
  return [idx[0], idx[1]];
}

const fmt2 = (n: number): string => n.toFixed(2);

/**
 * Format display lines for the annotation's textBox. Lines are short and
 * unit-suffixed; all physical values render with two decimals. Time (ms) stays
 * integer since sub-millisecond precision has no clinical meaning here.
 */
export function formatDopplerTextLines(
  variant: SpectralDopplerToolVariant,
  values: SpectralDopplerValues
): string[] {
  const lines: string[] = [];

  const mPerSec = (cmS?: number) => (cmS === undefined ? undefined : cmS / 100);

  if (variant === 'probe') {
    if (Number.isFinite(values.peakV_cmS)) {
      // Magnitude — the peak velocity is reported positive regardless of which
      // side of the baseline it sits on (the stored value stays signed).
      lines.push(`V: ${fmt2(Math.abs(mPerSec(values.peakV_cmS)!))} m/s`);
    }
    if (Number.isFinite(values.peakGradient_mmHg)) {
      lines.push(`PG: ${fmt2(values.peakGradient_mmHg as number)} mmHg`);
    }
    if (Number.isFinite(values.timeMs)) {
      lines.push(`t: ${Math.round(values.timeMs as number)} ms`);
    }
    return lines;
  }

  if (variant === 'segment') {
    if (Number.isFinite(values.v1_cmS) && Number.isFinite(values.v2_cmS)) {
      lines.push(
        `V1→V2: ${fmt2(mPerSec(values.v1_cmS)!)} → ${fmt2(mPerSec(values.v2_cmS)!)} m/s`
      );
    }
    if (Number.isFinite(values.slope_cmS2)) {
      lines.push(`Slope: ${fmt2((values.slope_cmS2 as number) / 100)} m/s²`);
    }
    if (Number.isFinite(values.meanGradient_mmHg)) {
      lines.push(
        `Mean Gradient: ${fmt2(values.meanGradient_mmHg as number)} mmHg`
      );
    }
    return lines;
  }

  // VTI
  if (Number.isFinite(values.vti_cm)) {
    lines.push(`VTI: ${fmt2(values.vti_cm as number)} cm`);
  }
  // VTI is direction-agnostic (∫|v|dt), so the peak velocity displayed here
  // is the magnitude. Some DICOM vendors encode physicalDeltaY with the
  // opposite sign convention, which would otherwise flip peakV_cmS sign.
  if (Number.isFinite(values.peakV_cmS)) {
    lines.push(`Peak: ${fmt2(Math.abs(mPerSec(values.peakV_cmS)!))} m/s`);
  }
  if (Number.isFinite(values.meanV_cmS)) {
    lines.push(`Mean: ${fmt2(Math.abs(mPerSec(values.meanV_cmS)!))} m/s`);
  }
  if (Number.isFinite(values.durationMs)) {
    lines.push(`Δt: ${Math.round(values.durationMs as number)} ms`);
  }
  return lines;
}

/** Shape of the metadata payload registered under the `ultrasoundRegions` key. */
export interface UltrasoundRegionsMeta {
  regions: UltrasoundRegion[];
}

export interface SpectralDopplerAnnotationData {
  spectralCalibration?: SpectralCalibration;
  spectralValues?: SpectralDopplerValues;
  spectralToolVariant?: SpectralDopplerToolVariant;
  /** Image-pixel coords of the handle points at completion time. */
  spectralPixelPoints?: [number, number][];
}

export function markSpectralAnnotationData(
  annotationData: Record<string, unknown>,
  variant: SpectralDopplerToolVariant,
  calibration: SpectralCalibration,
  values: SpectralDopplerValues,
  pixelPoints: [number, number][]
): void {
  Object.assign(annotationData, {
    spectralToolVariant: variant,
    spectralCalibration: calibration,
    spectralValues: values,
    spectralPixelPoints: pixelPoints,
  });
}
